import { useEffect, useMemo, useState } from "react";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import QuotesHeatmap from "../../components/quotes/QuotesHeatmap";
import useQuotes from "../../data/useQuotes";
import { IQuote } from "../../models/Quote";
import { geocodePostcodes, LatLng } from "../../utils/geocode";
import LoadingWheel from "../../components/shared/LoadingWheel";

const DEFAULT_RANGE_DAYS = 30;

const startOfDay = (d: Date) => {
    const r = new Date(d);
    r.setHours(0, 0, 0, 0);
    return r;
};

const startOfNextDay = (d: Date) => {
    const r = startOfDay(d);
    r.setDate(r.getDate() + 1);
    return r;
};

const defaultRange = (): DateValueType => {
    const end = startOfDay(new Date());
    const start = new Date(end);
    start.setDate(start.getDate() - (DEFAULT_RANGE_DAYS - 1));
    return { startDate: start, endDate: end };
};

export default function QuoteMap() {
    const [range, setRange] = useState<DateValueType>(defaultRange);

    // Convert the inclusive picker range into the API's [from, to) window. The picker may hand back
    // Date objects or "YYYY-MM-DD" strings depending on version, so normalise via new Date().
    const { from, to } = useMemo(() => {
        const start = range?.startDate ? new Date(range.startDate) : null;
        const end = range?.endDate ? new Date(range.endDate) : null;
        return {
            from: start ? startOfDay(start).toISOString() : null,
            to: end ? startOfNextDay(end).toISOString() : null,
        };
    }, [range]);

    const { abandoned } = useQuotes({ step: 0, from, to });

    const [coordsMap, setCoordsMap] = useState<Record<string, LatLng>>({});
    const [geocoding, setGeocoding] = useState(false);

    const { setInfo } = usePageNameContext();

    useEffect(() => {
        setInfo({
            name: "Quote Map",
            desc: "View a heatmap of your quotes",
        });
    }, []);

    const quotes: IQuote[] = abandoned.data?.data ?? [];

    // Geocode any postcodes we haven't already resolved. Results are cached across range changes.
    useEffect(() => {
        if (quotes.length === 0) return;

        const missing = Array.from(new Set(quotes.map((q) => q.postcode))).filter((pc) => pc && !(pc in coordsMap));
        if (missing.length === 0) return;

        let cancelled = false;
        setGeocoding(true);
        geocodePostcodes(missing)
            .then((map) => {
                if (cancelled) return;
                setCoordsMap((prev) => ({ ...prev, ...map }));
            })
            .finally(() => {
                if (!cancelled) setGeocoding(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotes]);

    const points = useMemo<[number, number, number][]>(() => {
        const pts: [number, number, number][] = [];
        quotes.forEach((q) => {
            const ll = coordsMap[q.postcode];
            if (ll && ll[0] && ll[1]) pts.push([ll[0], ll[1], 1]);
        });
        return pts;
    }, [quotes, coordsMap]);

    const loading = abandoned.isLoading || geocoding;

    return (
        <>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-full sm:w-80">
                    <Datepicker
                        primaryColor="blue"
                        value={range}
                        onChange={(val) => setRange(val)}
                        showShortcuts
                        showFooter
                        useRange
                        maxDate={new Date()}
                        displayFormat="DD/MM/YYYY"
                        separator="to"
                        placeholder="Select a date range"
                        popoverDirection="down"
                        inputClassName="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                </div>
                {!loading && (
                    <span className="text-sm text-gray-500">
                        {points.length} of {quotes.length} quote{quotes.length === 1 ? "" : "s"} plotted
                    </span>
                )}
            </div>

            {loading ? (
                <div className="mt-4 min-h-[50vh] p-4 bg-white rounded-lg shadow">
                    <LoadingWheel />
                </div>
            ) : (
                <div className="h-[70vh] w-full rounded-lg shadow-md bg-white overflow-hidden mt-4">
                    {points.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-sm text-gray-500">
                            No quotes found for the selected date range.
                        </div>
                    ) : (
                        <QuotesHeatmap points={points} />
                    )}
                </div>
            )}
        </>
    );
}
